import { AxiosError } from 'axios'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChapterParams } from '@/types/chapter'
import { ChapterResponse, ClassChapterRes } from '@/types/module'
import { axiosInstance } from '@/lib/axios'
import BaseService from './baseService'

interface ParamsProps {
  module_id: string
  limit: number
  page: number
  search?: string
}

export const deleteChapter = async (chapter_id: string | number) => {
  return axiosInstance.delete(`/module/chapters/${chapter_id}`)
}

export const getChapters = async ({
  module_id,
  page,
  limit,
  search,
}: ParamsProps) => {
  return axiosInstance.get<ChapterResponse>(
    `/module/${module_id}/chapters?page=${page}&limit=${limit}&search=${search}`
  )
}

export const getChapterByClass = async ({
  module_id,
  page,
  limit,
  search,
}: ParamsProps) => {
  return axiosInstance.get<ClassChapterRes>(
    `/class/${module_id}/chapters?page=${page}&limit=${limit}&search=${search}`
  )
}

export const useDeleteChapter = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteChapter,
    mutationKey: ['delete_chapter'],
  })

  return {
    DeleteChapterAsync: mutateAsync,
    isDeletingChapter: isPending,
  }
}

export const useGetChapter = ({
  module_id,
  page,
  limit,
  search,
}: ParamsProps) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['chapter', module_id, page, limit, search],
    queryFn: () => getChapters({ module_id, page, limit, search }),
    enabled: !!module_id,
  })

  return {
    chapters: data?.data.data ?? [],
    meta: data?.data.meta,
    isLoadingChapter: isLoading,
    refetchChapter: refetch,
  }
}

export const useGetChapterByClass = ({
  module_id,
  page,
  limit,
  search,
}: ParamsProps) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['chapter', module_id, page, limit, search],
    queryFn: () => getChapterByClass({ module_id, page, limit, search }),
    enabled: !!module_id,
  })

  return {
    classChapters: data?.data.data ?? [],
    meta: data?.data.meta,
    isLoadingClassChapter: isLoading,
    refetchClassChapter: refetch,
  }
}

class ChapterService extends BaseService {
  async getChapters(params: ChapterParams): Promise<ChapterResponse> {
    try {
      const response = await this.get<ChapterResponse>(
        `/module/${params.module_id}/chapters`,
        params
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to fetch chapters'
        )
      }
      throw error
    }
  }
}

export const chapterService = new ChapterService()
